import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../../utils/api';

// Cache for finish data to avoid repeated API calls
// Keyed by appliesTo value to support different product types
const finishDataCacheByType = {};
const cachePromiseByType = {};

/**
 * Hook to fetch and manage finish data from the backend
 * @param {string} appliesTo - Filter by product type: 'windows', 'patiodoors', or undefined for all
 * Data structure from API:
 * {
 *   frameInternalColors: [{ id, label, value, applies_to, active, sort_order }],
 *   frameExternalColors: [{ id, label, value, applies_to, active, sort_order }]
 * }
 */
export const useFinishData = (appliesTo = 'windows') => {
  const cacheKey = appliesTo || 'all';
  const [data, setData] = useState(finishDataCacheByType[cacheKey]);
  const [loading, setLoading] = useState(!finishDataCacheByType[cacheKey]);
  const [error, setError] = useState(null);

  const fetchFinishData = useCallback(async (forceRefresh = false) => {
    // Return cached data if available and not forcing refresh
    if (finishDataCacheByType[cacheKey] && !forceRefresh) {
      setData(finishDataCacheByType[cacheKey]);
      setLoading(false);
      return finishDataCacheByType[cacheKey];
    }

    // If already fetching, wait for that promise
    if (cachePromiseByType[cacheKey] && !forceRefresh) {
      try {
        const result = await cachePromiseByType[cacheKey];
        setData(result);
        setLoading(false);
        return result;
      } catch (e) {
        setError(e.message);
        setLoading(false);
        return null;
      }
    }

    setLoading(true);
    setError(null);

    // Build URL with optional applies_to filter
    const url = appliesTo ? `/finish/?applies_to=${appliesTo}` : '/finish/';

    cachePromiseByType[cacheKey] = authFetch(url)
      .then((result) => {
        finishDataCacheByType[cacheKey] = result;
        setData(result);
        setLoading(false);
        return result;
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
        cachePromiseByType[cacheKey] = null;
        throw e;
      });

    return cachePromiseByType[cacheKey];
  }, [appliesTo, cacheKey]);

  useEffect(() => {
    if (!finishDataCacheByType[cacheKey]) {
      fetchFinishData();
    }
  }, [fetchFinishData, cacheKey]);

  // Get active frame internal colors
  const getFrameInternalColors = useCallback(() => {
    if (!data?.frameInternalColors) return [];
    return data.frameInternalColors
      .filter(c => c.active)
      .map(c => ({ value: c.value, label: c.label, color: c.value }));
  }, [data]);

  // Get active frame external colors
  const getFrameExternalColors = useCallback(() => {
    if (!data?.frameExternalColors) return [];
    return data.frameExternalColors
      .filter(c => c.active)
      .map(c => ({ value: c.value, label: c.label, color: c.value }));
  }, [data]);

  // Clear cache (useful for admin updates)
  const clearCache = useCallback(() => {
    Object.keys(finishDataCacheByType).forEach(key => {
      delete finishDataCacheByType[key];
    });
    Object.keys(cachePromiseByType).forEach(key => {
      delete cachePromiseByType[key];
    });
  }, []);

  return {
    data,
    loading,
    error,
    refresh: () => fetchFinishData(true),
    clearCache,
    getFrameInternalColors,
    getFrameExternalColors
  };
};

export default useFinishData;
